import { Maximize2, Minimize2, RotateCcw } from 'lucide-react'
import { useEffect, useState, type ReactElement, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Badge } from 'src/shared/components/ui/badge'
import { Tooltip } from 'src/shared/components/ui/tooltip'
import { SALES_APPROVED_QUOTE_STATUS } from 'src/shared/consts/quoteStatus'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import type { RequestTabId } from 'src/pages/_myAccount/Requests/Request/types'
import { DemoWorkspace } from './DemoWorkspace'

const FRAME_CLASS_NAME =
  'relative h-[min(68dvh,620px)] w-full min-w-0 overflow-hidden overscroll-contain rounded-2xl border border-stone-200 bg-white shadow-[0_12px_40px_rgba(28,25,23,0.08)] md:h-[min(85dvh,880px)]'

const DemoFullscreenOverlay = ({
  ariaLabel,
  children,
}: {
  ariaLabel: string
  children: ReactNode
}): ReactElement | null => {
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 z-[100] overflow-hidden bg-[#f4f5f7]"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <div className="relative h-full w-full overflow-hidden">{children}</div>
    </div>,
    document.body
  ) as ReactElement
}

export const HowToSection = () => {
  const { t } = useLocalization()
  const [tab, setTab] = useState<RequestTabId>('explore')
  const [demoKey, setDemoKey] = useState(0)
  const [expanded, setExpanded] = useState(false)
  const [checkoutStatus, setCheckoutStatus] = useState(
    SALES_APPROVED_QUOTE_STATUS
  )

  const resetDemo = () => {
    setTab('explore')
    setCheckoutStatus(SALES_APPROVED_QUOTE_STATUS)
    setDemoKey((key) => key + 1)
  }

  const exitFullscreen = () => setExpanded(false)

  useEffect(() => {
    if (!expanded) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [expanded])

  const toolbar = (
    <Row className="items-center gap-2">
      <Tooltip
        delayDuration={150}
        side="bottom"
        content={t(
          'Start the walkthrough over. Nothing here is a real request.',
          'Börja om demon. Inget här är en riktig förfrågan.'
        )}
      >
        <Badge
          variant="outline"
          size="md"
          clickable
          className="gap-1.5 bg-white/80 font-medium text-neutral-500"
          onClick={resetDemo}
        >
          <RotateCcw className="size-3.5" strokeWidth={2} />
          {t('Reset demo', 'Återställ demo')}
        </Badge>
      </Tooltip>
      <Tooltip
        delayDuration={150}
        side="bottom"
        content={
          expanded
            ? t('Exit fullscreen', 'Avsluta helskärm')
            : t('Open as a full request', 'Öppna som en riktig förfrågan')
        }
      >
        <Badge
          variant="outline"
          size="md"
          clickable
          className="bg-white/80 px-2 font-medium text-neutral-500"
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t('Exit fullscreen', 'Avsluta helskärm')
              : t('Open as a full request', 'Öppna som en riktig förfrågan')
          }
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? (
            <Minimize2 className="size-3.5" strokeWidth={2} />
          ) : (
            <Maximize2 className="size-3.5" strokeWidth={2} />
          )}
        </Badge>
      </Tooltip>
    </Row>
  )

  const framedToolbar = (
    <div className="absolute top-3 left-3 z-50">{toolbar}</div>
  )

  const demo = (headerActions?: ReactNode) => (
    <DemoWorkspace
      key={demoKey}
      tab={tab}
      onTabChange={setTab}
      checkoutStatus={checkoutStatus}
      onCheckoutStatusChange={setCheckoutStatus}
      fullscreen={expanded}
      onExitFullscreen={exitFullscreen}
      headerActions={headerActions}
    />
  )

  return (
    <div id="how-it-works" className="w-full scroll-mt-24">
      <Column className="w-full items-center gap-8 md:gap-10">
        <Column className="max-w-2xl items-center gap-4 text-center">
          <h2 className="max-w-full text-balance text-4xl font-normal tracking-tight text-neutral-900 md:text-5xl">
            {t(
              'Each order is a conversation.',
              'Varje order är en konversation.'
            )}
          </h2>
          <p className="max-w-xl text-lg leading-relaxed text-neutral-900 md:text-xl">
            {t(
              'You already buy merch by writing to someone. This feels the same. One thread per order, with the brief, products and status in the same place.',
              'Ni köper redan merch genom att skriva till någon. Det här känns likadant. En tråd per order, med brief, produkter och status på samma ställe.'
            )}
          </p>
        </Column>

        <Column className="mx-auto w-full min-w-0 max-w-6xl items-center md:[zoom:0.8]">
          {expanded ? (
            <div className={FRAME_CLASS_NAME} aria-hidden />
          ) : (
            <div className={FRAME_CLASS_NAME}>
              {demo()}
              {framedToolbar}
            </div>
          )}
        </Column>
      </Column>

      {expanded ? (
        <DemoFullscreenOverlay ariaLabel={t('Demo request', 'Demo-förfrågan')}>
          {demo(toolbar)}
        </DemoFullscreenOverlay>
      ) : null}
    </div>
  )
}
